import { Button } from "@package/subframe/components/Button";
import { useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useRequestEventRecordAcknowledgementCommandControllerRequest } from "@/generated/event-records/event-records";
import { useListNurseryMembersQueryControllerListMembers } from "@/generated/members/members";
import type {
  RequestEventRecordAcknowledgementDtoScopeRole,
  RequestEventRecordAcknowledgementDtoScopeType,
} from "@/generated/model";
import { RequestEventRecordAcknowledgementDtoReminderFrequency } from "@/generated/model";
import {
  emptyScopeDraft,
  isScopeValid,
  type ScopeDraft,
  scopeToDto,
} from "@/lib/acknowledgements/scope";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";
import {
  canBeAskedToAcknowledge,
  type NurseryMemberOption,
} from "@/lib/member-display";

import { AcknowledgementScopePicker } from "./AcknowledgementScopePicker";

interface RequestAcknowledgementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventRecordId: string;
  nurseryId: string;
  /** Excluded from the list — asking yourself is not a thing. */
  currentUserId: string;
  alreadyRequestedUserIds: string[];
  onRequested: () => Promise<unknown> | void;
}

/**
 * Asks named individuals to acknowledge a record (NAU-342 AC 6).
 *
 * The copy is deliberate about what this does and does not do: their reply is
 * recorded against the record but does not close it, and does not replace whoever
 * the nursery's rules make responsible. Without that, "acknowledge" would look like
 * one thing with two meanings.
 */
export function RequestAcknowledgementDialog({
  open,
  onOpenChange,
  eventRecordId,
  nurseryId,
  currentUserId,
  alreadyRequestedUserIds,
  onRequested,
}: RequestAcknowledgementDialogProps) {
  const requestAcknowledgement =
    useRequestEventRecordAcknowledgementCommandControllerRequest();
  const [draft, setDraft] = useState<ScopeDraft>(emptyScopeDraft());
  const [dueAt, setDueAt] = useState("");
  const [reminderFrequency, setReminderFrequency] =
    useState<RequestEventRecordAcknowledgementDtoReminderFrequency>(
      RequestEventRecordAcknowledgementDtoReminderFrequency.NONE,
    );

  const { data: membersData, isLoading: isMembersLoading } =
    useListNurseryMembersQueryControllerListMembers(
      { nurseryId, limit: 100 },
      { query: { enabled: open && !!nurseryId } },
    );

  const members: (NurseryMemberOption & { role?: string })[] = (
    membersData?.data?.data ?? []
  )
    .filter((member) => member.userId !== currentUserId)
    // Only roles that can read the record — see canBeAskedToAcknowledge.
    .filter((member) => canBeAskedToAcknowledge(member.role))
    .map((member) => ({
      userId: member.userId,
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      role: member.role,
    }));

  const close = () => {
    setDraft(emptyScopeDraft());
    setDueAt("");
    setReminderFrequency(
      RequestEventRecordAcknowledgementDtoReminderFrequency.NONE,
    );
    onOpenChange(false);
  };

  const submit = async () => {
    try {
      // scopeToDto narrows the draft to the fields the chosen mode allows: the
      // API rejects a ROLE scope that still carries the names the picker was
      // remembering.
      const scope = scopeToDto(draft);
      const response = await requestAcknowledgement.mutateAsync({
        eventRecordId,
        data: {
          // Still sent for a named scope, and still the field this endpoint has
          // always taken.
          userIds: scope.staffUserIds ?? [],
          scopeType:
            scope.scopeType as unknown as RequestEventRecordAcknowledgementDtoScopeType,
          scopeRole:
            (scope.scopeRole as unknown as RequestEventRecordAcknowledgementDtoScopeRole) ??
            undefined,
          dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
          reminderFrequency,
        },
      });
      // Counted from the response rather than from the selection, because an
      // ALL_STAFF or ROLE scope is resolved server-side — the client does not
      // know how many people it reached.
      const asked = response.data.requests.length;
      toast.success(
        asked === 1
          ? "Acknowledgement requested"
          : `Acknowledgement requested from ${asked} people`,
      );
      await onRequested();
      close();
    } catch (error) {
      toast.error(
        extractApiErrorMessage(error, "Failed to request acknowledgement"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogTitle>Request acknowledgement</DialogTitle>
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-body font-body text-subtext-color">
            Choose who has to acknowledge this, from the managers and deputy
            managers who can open the record. The record shows in Awaiting
            acknowledgement until they sign it. Their reply is recorded on the
            record but does not resolve it, and does not replace whoever your
            nursery&apos;s rules make responsible for signing it off.
          </span>

          <AcknowledgementScopePicker
            draft={draft}
            onChange={setDraft}
            members={members}
            isMembersLoading={isMembersLoading}
            disabled={requestAcknowledgement.isPending}
            alreadyRequestedUserIds={alreadyRequestedUserIds}
            dueAt={dueAt}
            onDueAtChange={setDueAt}
            reminderFrequency={reminderFrequency}
            onReminderFrequencyChange={setReminderFrequency}
          />

          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="neutral-secondary"
              onClick={close}
              disabled={requestAcknowledgement.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={submit}
              disabled={
                !isScopeValid(draft) || requestAcknowledgement.isPending
              }
              loading={requestAcknowledgement.isPending}
            >
              Send request
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
